import { proxy, subscribe } from "valtio";
import { derive } from "valtio/utils";
import jwt_decode, { type JwtPayload } from "jwt-decode";

import {
  IAccessTokenDto,
  IDealDto,
  IPurchaserDto,
} from "common/middleware/api.services";
import {
  getFromLocalStorage,
  getObjFromStorageOrDefault,
  setItemIfItExists,
  removeItemIfExists,
} from "common/middleware/localStorage";

type Store = {
  purchaseOfferId?: string;
  selectionId?: string;
  currentPurchaseOfferState?: string;
  purchaseOffer?: IDealDto;
  purchasers?: IPurchaserDto[];
  isLoggedIn?: boolean;
  isInitialVerifyCodeSent: boolean; // this can prevent us from sending the code to the user if they refresh the page / come back
  accessToken?: IAccessTokenDto;
  env: "dev" | "prod";
  currentlyRefreshingAccessToken: boolean;
  currentProjectId: string;
  machineToken: IAccessTokenDto;
  isSigningOut: boolean;
  firstVisitTime?: string;
  builderId: string;
};

const localState = proxy<Store>({
  purchaseOfferId: getFromLocalStorage("purchaseOfferId"),
  selectionId: getFromLocalStorage("selectionId"),
  accessToken: getObjFromStorageOrDefault("accessToken", {}),
  isInitialVerifyCodeSent: getObjFromStorageOrDefault(
    "isInitialVerifyCodeSent",
    false
  ),
  // assume prod by default
  env: process.env.NEXT_PUBLIC_ENV === "dev" ? "dev" : "prod",
  currentlyRefreshingAccessToken: false,
  currentProjectId: getFromLocalStorage("projectId"),
  machineToken: getObjFromStorageOrDefault("machineToken", {}),
  isSigningOut: false,
  firstVisitTime: getFromLocalStorage("firstVisitTime"),
  builderId: getFromLocalStorage("builderId"),
});

const store = derive(
  {
    // User access tokens have a refresh token but machine ones don't
    isLoggedIn: (get: any) =>
      get(localState).accessToken?.token !== undefined,
    userId: (get: any) =>
      get(localState).accessToken?.token
        ? jwt_decode<JwtPayload & { sub: string }>(
          get(localState).accessToken?.token
        ).sub
        : undefined,
    isDevEnv: (get: any) => get(localState).env === "dev",
    isLicensedSeller: (get: any) => get(localState).builderId !== undefined,
    isPropertyDevelopmentProject: (get: any) => get(localState).currentProjectId !== undefined,
    userType: (get: any) => get(localState).accessToken?.token
      ? jwt_decode<JwtPayload & { user_type: string }>(get(localState).accessToken?.token).user_type
      : undefined,
  },
  {
    proxy: localState,
  }
);

export const setPurchaseOfferId = (
  purchaseOfferId: string,
  purchaseOfferState?: string
) => {
  store.purchaseOfferId = purchaseOfferId;
  store.currentPurchaseOfferState = purchaseOfferState;
};

export const setSelectionId = (selection: string) => {
  store.selectionId = selection;
};

export const setProjectId = (projectId: string) => {
  store.currentProjectId = projectId;
};

export const updatePurchaseOffer = (purchaseOffer: IDealDto) => {
  console.log("updatePurchaseOffer", purchaseOffer);
  store.purchaseOfferId = purchaseOffer.id;
  store.purchaseOffer = purchaseOffer;
};

export const updateAccessToken = (accessToken: IAccessTokenDto) => {
  Object.assign(store.accessToken, accessToken);
};

export const updateMachineToken = (token: IAccessTokenDto) => {
  store.machineToken = token
}

export const clearAll = () => {
  clearAccessToken();
  clearMachineToken();
  store.purchaseOfferId = undefined;
  store.selectionId = undefined;
  store.isInitialVerifyCodeSent = false;
  store.currentProjectId = undefined;
  store.builderId = undefined;
  sessionStorage.removeItem("registration");
};

export const clearAccessToken = () => {
  store.accessToken = {};
  store.isInitialVerifyCodeSent = false;
};

export const clearMachineToken = () => {
  store.machineToken = undefined;
}

export const updateLoggedInStatus = (state: boolean) => {
  store.isLoggedIn = state;
};

export const updatePurchasers = (purchasers: IPurchaserDto[]) => {
  store.purchasers = purchasers;
};

subscribe(store, () => {
  if (typeof window === "undefined") {
    return undefined;
  }
  if (!store.accessToken?.token) {
    removeItemIfExists("accessToken");
  } else {
    setItemIfItExists("accessToken", JSON.stringify(store.accessToken), {});
  }

  if (!store.machineToken?.token) {
    localStorage.removeItem("machineToken");
  } else {
    localStorage.setItem("machineToken", JSON.stringify(store.machineToken));
  }

  if (store.purchaseOfferId) {
    setItemIfItExists("purchaseOfferId", store.purchaseOfferId, {});
  } else {
    removeItemIfExists("purchaseOfferId");
  }

  if (store.currentProjectId) {
    setItemIfItExists("projectId", store.currentProjectId, {});
  } else {
    removeItemIfExists("projectId");
  }

  if (store.selectionId) {
    setItemIfItExists("selectionId", store.selectionId, {});
  } else {
    removeItemIfExists("selectionId");
  }

  if (store.builderId) {
    localStorage.setItem("builderId", store.builderId);
  } else {
    localStorage.removeItem("builderId");
  }

  setItemIfItExists(
    "isInitialVerifyCodeSent",
    store.isInitialVerifyCodeSent.toString(),
    {}
  );
});

export default store;
